import React from 'react';
import throttle from 'lodash/throttle';
import 'styles/sharing/DirectCallSharingDialog.styl';
import SharingActions from 'actions/sharing/SharingActions';
import FileUploadSharingStore from 'stores/sharing/FileUploadSharingStore';
import FileUploadSharingDialogFormContainer from 'components/stateful/sharing/dialog/FileUploadSharingDialogFormContainer';
import FileUploadMetadata from 'components/pure/call-card/FileUploadMetadata';

/**
 * <DirectCallSharingDialog /> is a modal dialog that shows a form which allows a user to
 * provide info for a call to be shared
 */
class DirectCallSharingDialog extends React.Component {
  constructor(props, context) {
    super(props, context);
    this.getScrollDiv = this.getScrollDiv.bind(this);
    this.handleScroll = this.handleScroll.bind(this);
    this.throttledScrollHandler = null;
    this.scrollDiv = React.createRef();
  }

  isShareDisabled = () => {
    return (
      this.props.isSharingFileUploadLoading ||
      !FileUploadSharingStore.getState().get('shareDataError').isEmpty()
    );
  };

  /** Share the current FileUpload using data provided in this <Dialog /> component */
  shareFileUpload = throttle(() => {
    const {fileUpload, onShare, slackNotify} = this.props;
    SharingActions.shareFileUpload([fileUpload, onShare, slackNotify]);
    // After the first submit, we listen to scrolls and try to adjust the error Popovers, if any.
    // It is okay to do that only at the first share because we don't show errors until the user tries to submit.
    // We add the scroll listener here to ensure that the scrolling div is in the HTML DOM.
    this.addScrollListener();
  }, 1000);

  addScrollListener() {
    if (!this.throttledScrollHandler) {
      this.throttledScrollHandler = throttle(this.handleScroll, 300, {
        trailing: true,
      });
      this.scrollDiv?.current.addEventListener('scroll', this.throttledScrollHandler);
    }
  }

  componentDidMount() {
    this.props.onLoad(this.shareFileUpload, this.isShareDisabled);
  }

  componentDidUpdate(prevProps) {
    if (!this.props.open) {
      if (this.scrollDiv?.current && this.throttledScrollHandler) {
        this.scrollDiv.current.removeEventListener('scroll', this.throttledScrollHandler);
        this.throttledScrollHandler = null;
      }
    }

    // switching to tab
    if (this.props.visible && !prevProps.visible) {
      // if we're visible, set up our submit/disabled logic.
      this.props.onLoad(this.shareFileUpload, this.isShareDisabled);
    }

    // switching away from tab
    if (!this.props.visible && prevProps.visible) {
      // prompt the dialog to clear sharing information
      SharingActions.switchTabs();
    }
  }

  handleScroll() {
    if (this.scrollDiv?.current) {
      // fake a sharingDataChanged to force the error popovers, if any, to re-anchor to the right places
      SharingActions.sharingDataChanged(this.props.fileUpload);
    }
  }

  getScrollDiv() {
    return this.scrollDiv?.current;
  }

  render() {
    const topErrorAnchor = (
      <div
        className="top-error-anchor"
        ref={c => {
          this.topAnchorDiv = c;
        }}
      />
    );
    const bottomErrorAnchor = (
      <div
        className="bottom-error-anchor"
        ref={c => {
          this.bottomAnchorDiv = c;
        }}
      />
    );
    return (
      <div
        ref={c => {
          this.boundingDiv = c;
        }}>
        {topErrorAnchor}
        <div className="share-call-scroll-div" ref={this.scrollDiv}>
          <FileUploadMetadata
            fileUpload={this.props.fileUpload}
            duration={this.props.fileUpload.fields.Duration}
          />
          <FileUploadSharingDialogFormContainer
            submitted={this.props.submitted}
            fileUpload={this.props.fileUpload}
            boundingDiv={this.boundingDiv}
            scrollDiv={this.getScrollDiv}
            topErrorAnchor={this.topAnchorDiv}
            bottomErrorAnchor={this.bottomAnchorDiv}
          />
        </div>
        {bottomErrorAnchor}
      </div>
    );
  }
}

export default DirectCallSharingDialog;
